/**
 * usePinnedDrawer — per-device pinned-state for the kanban CardDetailDrawer,
 * plus the CSS-variable + width constants the drawer publishes.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * Behaviour is unchanged — this is a pure structural move.
 *
 * Pin-as-split-pane: when the operator pins the drawer, it stops being a
 * modal overlay and becomes a docked rail on the right of the viewport.
 * The kanban board (KanbanBoardView / PlannerPage) reads `PINNED_CSS_VAR`
 * and adds matching right-padding so its content shrinks to make room
 * instead of being hidden under the rail. Single source of truth lives
 * here; consumers opt in with
 * `padding-right: var(--quotech-kanban-pinned-rail-w, 0px)`.
 */
import { useCallback, useEffect, useState } from 'react'

export const PINNED_LS_KEY = 'quotech.kanban.cardDetailPinned'
export const PINNED_CSS_VAR = '--quotech-kanban-pinned-rail-w'
export const PINNED_RAIL_WIDTH_PX = 420
export const OVERLAY_RAIL_WIDTH_PX = 448 // Tailwind `max-w-md` = 28rem
export const PINNED_SYNC_EVENT = 'quotech:kanban-card-detail-pinned'

// Occlusion variable — published whenever the drawer is open in EITHER
// mode (overlay or pinned). Read by `UniversalDock` to shift its
// fixed-position pill leftwards so the dock never overlaps the
// drawer's bottom-right (which previously hid the panel's footer
// actions and trapped wheel events on the dock instead of the panel,
// making it feel like scroll was broken). Distinct from
// `PINNED_CSS_VAR` because the kanban board content only shrinks in
// pinned mode — the dock needs to dodge the panel in BOTH modes.
export const OCCLUSION_CSS_VAR = '--quotech-kanban-drawer-w'

export function readPinnedFromStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    // Three-state read so the default for new operators is PINNED
    // (docked rail, no modal backdrop), matching the right-side
    // email-allocation rail's visual idiom. Operators who explicitly
    // unpinned the drawer (toggle → '0') keep their preference;
    // first-time visitors get the unified single-drawer experience.
    // Operator complaint 2026-05-15: "this is not a separate drawer
    // ... for when you click on the card.. i have told you to fix
    // this multiple times" — the unpinned default opened the card
    // as a fixed-inset modal with `bg-black/30 backdrop-blur-sm`,
    // which read as a stacked floating drawer over the email rail
    // instead of the same-slot replacement the operator expected.
    const stored = window.localStorage.getItem(PINNED_LS_KEY)
    if (stored === null) return true
    return stored === '1'
  } catch {
    return true
  }
}

/** Per-device pinned-state for the kanban CardDetailDrawer. Returns
 *  `[pinned, setPinned]`. Cross-component sync via storage events
 *  (different tabs) and a custom event (same tab, multiple drawer
 *  consumers — e.g. KanbanBoardView + PlannerPage). */
export function useCardDetailPinned(): [boolean, (next: boolean) => void] {
  const [pinned, setPinnedState] = useState<boolean>(readPinnedFromStorage)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onStorage = (e: StorageEvent) => {
      if (e.key === PINNED_LS_KEY) setPinnedState(readPinnedFromStorage())
    }
    const onCustom = () => setPinnedState(readPinnedFromStorage())
    window.addEventListener('storage', onStorage)
    window.addEventListener(PINNED_SYNC_EVENT, onCustom)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(PINNED_SYNC_EVENT, onCustom)
    }
  }, [])

  const setPinned = useCallback((next: boolean) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(PINNED_LS_KEY, next ? '1' : '0')
    } catch {
      // localStorage may be unavailable (private mode, etc.); the
      // in-memory state still works for this session.
    }
    setPinnedState(next)
    window.dispatchEvent(new CustomEvent(PINNED_SYNC_EVENT))
  }, [])

  return [pinned, setPinned]
}
